import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { NAV, CONTACT } from "../mock";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDrop, setActiveDrop] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActiveDrop(null);
  }, [location.pathname]);

  // Navy blue brand background like the original site
  const headerBg = scrolled
    ? "bg-[#0b3a5f]/95 backdrop-blur-md shadow-lg"
    : "bg-[#0b3a5f]";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
    >
      {/* MOBILE: stacked centered logo + hamburger */}
      <div className="lg:hidden">
        <div className="relative flex items-center justify-center px-4 py-3">
          <button
            onClick={() => setOpen(!open)}
            className="absolute left-3 p-2 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Hang Zen Surf + Sea"
              className="h-20 w-auto object-contain"
            />
          </Link>
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            aria-label="Call"
            className="absolute right-3 p-2 text-white"
          >
            <Phone size={22} />
          </a>
        </div>
      </div>

      {/* DESKTOP */}
      <div className="hidden lg:flex max-w-7xl mx-auto px-6 items-center justify-between gap-6 py-2">
        <Link to="/" className="flex items-center shrink-0 group">
          <img
            src="/logo.png"
            alt="Hang Zen Surf + Sea"
            className="h-24 xl:h-28 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="flex items-center gap-1">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && setActiveDrop(item.label)}
              onMouseLeave={() => setActiveDrop(null)}
            >
              {item.path ? (
                <Link
                  to={item.path}
                  className={`px-3 py-2 text-sm font-semibold rounded-md transition-colors flex items-center gap-1 uppercase tracking-wide ${
                    location.pathname === item.path
                      ? "text-amber-300"
                      : "text-white hover:text-amber-300"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <button
                  className="px-3 py-2 text-sm font-semibold rounded-md text-white hover:text-amber-300 transition-colors flex items-center gap-1 uppercase tracking-wide"
                >
                  {item.label}
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${
                      activeDrop === item.label ? "rotate-180" : ""
                    }`}
                  />
                </button>
              )}

              {item.children && activeDrop === item.label && (
                <div className="absolute top-full left-0 pt-2 z-50">
                  <div className="bg-white rounded-lg shadow-xl border border-slate-100 py-2 min-w-[230px] animate-in fade-in slide-in-from-top-2">
                    {item.children.map((c) => (
                      <Link
                        key={c.path}
                        to={c.path}
                        className="block px-4 py-2 text-sm text-slate-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <a
            href={`tel:${CONTACT.phoneRaw}`}
            className="hidden xl:flex items-center gap-2 text-sm font-semibold text-white hover:text-amber-300 transition-colors"
          >
            <Phone size={16} />
            {CONTACT.phone}
          </a>
          <Link
            to="/book-today"
            className="inline-flex items-center px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-full shadow-sm transition-colors"
          >
            Book Today
          </Link>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="lg:hidden bg-[#0b3a5f] border-t border-white/10 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {NAV.map((item) => (
              <div key={item.label}>
                {item.path ? (
                  <Link
                    to={item.path}
                    className="block px-3 py-3 text-base font-semibold text-white hover:bg-white/10 rounded-md uppercase tracking-wide"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <>
                    <div className="px-3 py-2 text-xs font-bold text-amber-300 uppercase tracking-wider">
                      {item.label}
                    </div>
                    {item.children.map((c) => (
                      <Link
                        key={c.path}
                        to={c.path}
                        className="block px-6 py-2 text-sm text-white/90 hover:bg-white/10 rounded-md"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </>
                )}
              </div>
            ))}
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-amber-300"
            >
              <Phone size={18} />
              {CONTACT.phone}
            </a>
            <Link
              to="/book-today"
              className="block text-center mx-3 mt-3 px-5 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full"
            >
              Book Today
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
